import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { MotionConfig, LazyMotion, domAnimation } from 'framer-motion';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AvatarProvider, ProfileProvider } from '@/contexts/AvatarContext';

import Navbar from '@/components/Navbar';
import ProtectedRoute from '@/components/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import Footer from '@/components/Footer';
import LoadingSpinner from '@/components/LoadingSpinner';

// Lazy-load route pages so each page ships in its own chunk
const Home = lazy(() => import('@/pages/Home'));
const CreatePoll = lazy(() => import('@/pages/CreatePoll'));
const EditPoll = lazy(() => import('@/pages/EditPoll'));
const PollView = lazy(() => import('@/pages/PollView'));
const Login = lazy(() => import('@/pages/Login'));
const Signup = lazy(() => import('@/pages/Signup'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Polls = lazy(() => import('@/pages/Polls'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const Features = lazy(() => import('@/pages/Features'));
const HelpCenter = lazy(() => import('@/pages/HelpCenter'));
const HelpArticle = lazy(() => import('@/pages/HelpArticle'));
const Profile = lazy(() => import('@/pages/Profile'));
const Settings = lazy(() => import('@/pages/Settings'));

function App() {

  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
      <LazyMotion features={domAnimation} strict>
      <AuthProvider>
        <AvatarProvider>
          <ProfileProvider>
            <ThemeProvider>
              <LanguageProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 transition-colors duration-300">
              <Navbar />

              <main className="pt-16">
                <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner /></div>}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/help/articles/:slug" element={<HelpArticle />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/create" element={<CreatePoll />} />
                    <Route
                      path="/edit/:id"
                      element={
                        <ProtectedRoute>
                          <EditPoll />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/poll/:id" element={<PollView />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/polls"
                      element={
                        <ProtectedRoute>
                          <Polls />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/analytics"
                      element={
                        <ProtectedRoute>
                          <Analytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </main>

              <Footer />

              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'var(--toast-bg)',
                    color: 'var(--toast-color)',
                    border: '1px solid var(--toast-border)',
                  },
                  success: {
                    iconTheme: {
                      primary: '#22c55e',
                      secondary: '#ffffff',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#ffffff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </LanguageProvider>
      </ThemeProvider>
    </ProfileProvider>
    </AvatarProvider>
    </AuthProvider>
    </LazyMotion>
    </MotionConfig>
  </ErrorBoundary>
);
}

export default App;
